import { HotkeyAction, Hotkeys, Settings } from '../types';
import { apiService } from './apiService';
import { isTauri } from '../utils/isTauri';
import { invoke } from '@tauri-apps/api/core';

export class HotkeyService {
  private static instance: HotkeyService;
  private registeredHotkeys: Map<string, () => void> = new Map();
  private registeredAccelerators: Map<string, string> = new Map();
  private currentHotkeys: Hotkeys = {
    activateTextSelection: 'Ctrl+Shift+;',
    quickFix: 'Ctrl+Shift+F',
    toggleAppVisibility: 'Ctrl+;'
  };

  private constructor() {}

  public static getInstance(): HotkeyService {
    if (!HotkeyService.instance) {
      HotkeyService.instance = new HotkeyService();
    }
    return HotkeyService.instance;
  }

  public async initialize(): Promise<void> {
    try {
      // Load hotkey settings from backend
      const response = await apiService.getSettings();
      const settings = response.settings || {};
      
      this.currentHotkeys = {
        activateTextSelection: settings.hotkeys?.activateTextSelection || 'Ctrl+Shift+;',
        quickFix: settings.hotkeys?.quickFix || 'Ctrl+Shift+F',
        toggleAppVisibility: settings.hotkeys?.toggleAppVisibility || 'Ctrl+;'
      };

      console.log('Hotkey service initialized with:', this.currentHotkeys);
    } catch (error) {
      console.error('Failed to initialize hotkey service:', error);
      // Use defaults if loading fails
      this.currentHotkeys = {
        activateTextSelection: 'Ctrl+Shift+;',
        quickFix: 'Ctrl+Shift+F',
        toggleAppVisibility: 'Ctrl+;'
      };
    }
  }

  public registerHotkey(action: HotkeyAction, callback: () => void): void {
    if (!isTauri()) {
      console.warn('Hotkeys are only supported in Tauri environment');
      return;
    }

    this.registeredHotkeys.set(action, callback);
    
    // Register the hotkey with Tauri backend
    const hotkeyString = this.currentHotkeys[action];
    if (hotkeyString) {
      this.registerTauriHotkey(hotkeyString, action);
    }
  }

  private async registerTauriHotkey(hotkeyString: string, action: string): Promise<void> {
    try {
      console.log(`[hotkeys] registering global shortcut via backend: ${hotkeyString} -> ${action}`);

      // Call the backend command to register the hotkey
      await invoke('register_global_hotkey', { 
        hotkey: hotkeyString, 
        action: action 
      });

      this.registeredAccelerators.set(hotkeyString, action);
      console.log(`[hotkeys] successfully registered: ${hotkeyString} -> ${action}`);
    } catch (error) {
      console.error('Failed to register hotkey:', error);
    }
  }

  public async updateHotkey(action: HotkeyAction, newHotkey: string): Promise<void> {
    const oldHotkey = this.currentHotkeys[action];
    this.currentHotkeys[action] = newHotkey;
    
    // Re-register the hotkey if it exists
    if (this.registeredHotkeys.has(action)) {
      // Unregister the old hotkey first
      if (oldHotkey) {
        try {
          await invoke('unregister_global_hotkey', { hotkey: oldHotkey });
        } catch (error) {
          console.error('Failed to unregister old hotkey:', error);
        }
      }
      // Register the new hotkey
      await this.registerTauriHotkey(newHotkey, action);
    }
  }

  public getCurrentHotkeys(): Settings['hotkeys'] {
    return { ...this.currentHotkeys };
  }

  public unregisterHotkey(action: HotkeyAction): void {
    this.registeredHotkeys.delete(action);
  }

  public unregisterAllHotkeys(): void {
    this.registeredHotkeys.clear();
  }
}
